import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod/v4";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { apiRequest } from "@/lib/queryClient";
import React from "react";
import { 
  Layout, 
  Plus, 
  Edit, 
  Trash2, 
  Copy, 
  Eye,
  ArrowUp,
  ArrowDown,
  Settings,
  Palette,
  Type,
  Image as ImageIcon,
  Link as LinkIcon,
  MousePointer,
  Search,
  File,
  Sparkles
} from "lucide-react";
import type { 
  LandingPageSection, 
  InsertLandingPageSection,
  FeatureComparison,
  InsertFeatureComparison,
  PricingTemplate,
  ResellerConfig
} from "@shared/schema";
import { insertLandingPageSectionSchema, insertFeatureComparisonSchema } from "@shared/schema";

// Section types with descriptions
const SECTION_TYPES = [
  { value: "hero", label: "Hero Section", description: "Main banner with headline and call-to-action", icon: Type },
  { value: "features", label: "Features", description: "Product features and benefits showcase", icon: Sparkles },
  { value: "pricing", label: "Pricing", description: "Subscription plans and pricing table", icon: MousePointer },
  { value: "testimonials", label: "Testimonials", description: "Customer reviews and social proof", icon: Type },
  { value: "cta", label: "Call to Action", description: "Conversion-focused action section", icon: MousePointer },
  { value: "faq", label: "FAQ", description: "Frequently asked questions", icon: Type },
  { value: "footer", label: "Footer", description: "Links, contact info, and legal pages", icon: LinkIcon },
];

// Layout options
const LAYOUT_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "centered", label: "Centered" },
  { value: "split", label: "Split Layout" },
  { value: "grid", label: "Grid Layout" },
];

export default function LandingPages() {
  const [activeTab, setActiveTab] = useState("sections");
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingFeatureId, setEditingFeatureId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch data
  const { data: sections = [], isLoading: loadingSections } = useQuery<LandingPageSection[]>({
    queryKey: ["/api/landing-pages/sections"],
  });

  const { data: features = [], isLoading: loadingFeatures } = useQuery<FeatureComparison[]>({
    queryKey: ["/api/landing-pages/features"],
  });

  const { data: templates = [], isLoading: loadingTemplates } = useQuery<PricingTemplate[]>({
    queryKey: ["/api/landing-pages/pricing-templates"],
  });

  const { data: config } = useQuery<ResellerConfig>({
    queryKey: ["/api/reseller/config"],
  });

  // Section form
  const sectionForm = useForm<z.input<typeof insertLandingPageSectionSchema>, any, InsertLandingPageSection>({
    resolver: zodResolver(insertLandingPageSectionSchema),
    defaultValues: {
      sectionType: "hero",
      sectionName: "",
      title: "",
      subtitle: "",
      description: "",
      content: {},
      backgroundColor: "#ffffff",
      textColor: "#000000",
      accentColor: config?.primaryColor || "#3b82f6",
      layout: "default",
      padding: "medium",
      buttons: [],
      links: [],
      secondaryImages: [],
      seoKeywords: [],
      isActive: true,
      sortOrder: 0,
    },
  });

  // Feature form
  const featureForm = useForm<z.input<typeof insertFeatureComparisonSchema>, any, InsertFeatureComparison>({
    resolver: zodResolver(insertFeatureComparisonSchema),
    defaultValues: {
      featureCategory: "Core",
      featureName: "",
      featureDescription: "",
      featureType: "boolean",
      freeValue: "false",
      proValue: "true",
      enterpriseValue: "true",
      sortOrder: 0,
      isHighlighted: false,
    },
  });

  // Mutations
  const createSectionMutation = useMutation({
    mutationFn: async (data: InsertLandingPageSection) => {
      return apiRequest("POST", "/api/landing-pages/sections", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages/sections"] });
      sectionForm.reset();
      setEditingSectionId(null);
    },
    onError: (error) => {
    },
  });

  const updateSectionMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<LandingPageSection> }) => {
      return apiRequest("PUT", `/api/landing-pages/sections/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages/sections"] });
      setEditingSectionId(null);
    },
    onError: (error) => {
    },
  });

  const deleteSectionMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/landing-pages/sections/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages/sections"] });
    },
    onError: (error) => {
    },
  });

  const createFeatureMutation = useMutation({
    mutationFn: async (data: InsertFeatureComparison) => {
      return apiRequest("POST", "/api/landing-pages/features", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages/features"] });
      featureForm.reset();
      setEditingFeatureId(null);
    },
    onError: (error) => {
    },
  });

  const updateFeatureMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<FeatureComparison> }) => {
      return apiRequest("PUT", `/api/landing-pages/features/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages/features"] });
      setEditingFeatureId(null);
    },
    onError: (error) => {
    },
  });

  const applyTemplateMutation = useMutation({
    mutationFn: async (templateId: string) => {
      return apiRequest("POST", `/api/landing-pages/pricing-templates/${templateId}/apply`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages/sections"] });
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages/features"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscription-plans"] });
      queryClient.invalidateQueries({ queryKey: ["/api/reseller/ai-pricing"] });
    },
    onError: (error) => {
    },
  });

  const handleEditSection = (section: LandingPageSection) => {
    setEditingSectionId(section.id);
    sectionForm.reset({
      ...section,
      content: section.content || {},
      buttons: section.buttons || [],
      links: section.links || [],
      secondaryImages: section.secondaryImages || [],
      seoKeywords: section.seoKeywords || [],
    });
  };

  const handleEditFeature = (feature: FeatureComparison) => {
    setEditingFeatureId(feature.id);
    featureForm.reset(feature);
  };

  const onSubmitSection = (data: InsertLandingPageSection) => {
    if (editingSectionId) {
      updateSectionMutation.mutate({ id: editingSectionId, data });
    } else {
      createSectionMutation.mutate(data);
    }
  };

  const onSubmitFeature = (data: InsertFeatureComparison) => {
    if (editingFeatureId) {
      updateFeatureMutation.mutate({ id: editingFeatureId, data });
    } else {
      createFeatureMutation.mutate(data);
    }
  };

  const moveSection = async (sectionId: string, direction: 'up' | 'down') => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;

    const currentOrder = section.sortOrder;
    const newOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;
    
    await updateSectionMutation.mutateAsync({ 
      id: sectionId, 
      data: { sortOrder: newOrder } 
    });
  };

  if (loadingSections || loadingFeatures || loadingTemplates) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Layout className="w-8 h-8" />
            Landing Pages
          </h1>
          <p className="text-gray-600 mt-2">
            Build and customize your landing pages with granular control over every section
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => window.open("/", "_blank")}
            data-testid="button-preview-landing"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview Landing
          </Button>
          <Button
            onClick={() => window.location.href = "/pricing"}
            data-testid="button-preview-pricing"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview Pricing
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
          <TabsTrigger value="sections">Page Sections</TabsTrigger>
          <TabsTrigger value="features">Feature Matrix</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="seo">SEO & Analytics</TabsTrigger>
        </TabsList>

        {/* Page Sections Tab */}
        <TabsContent value="sections" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Landing Page Sections</h2>
            <Dialog>
              <DialogTrigger asChild>
                <Button data-testid="button-add-section">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Section
                </Button>
              </DialogTrigger>
              <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingSectionId ? "Edit Section" : "Add New Section"}
                  </DialogTitle>
                  <DialogDescription>
                    Configure the content and styling for this landing page section
                  </DialogDescription>
                </DialogHeader>
                
                <Form {...sectionForm}>
                  <form onSubmit={sectionForm.handleSubmit(onSubmitSection)} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={sectionForm.control}
                        name="sectionType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Section Type</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger data-testid="select-section-type">
                                  <SelectValue placeholder="Select section type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {SECTION_TYPES.map((type) => (
                                  <SelectItem key={type.value} value={type.value}>
                                    <div className="flex items-center gap-2">
                                      <type.icon className="w-4 h-4" />
                                      <div>
                                        <div className="font-medium">{type.label}</div>
                                        <div className="text-xs text-gray-500">{type.description}</div>
                                      </div>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={sectionForm.control}
                        name="sectionName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Section Name</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="e.g., Main Hero, Feature Showcase"
                                {...field}
                                data-testid="input-section-name"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={sectionForm.control}
                        name="title"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Title</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Section title"
                                {...field}
                                value={field.value || ""}
                                data-testid="input-section-title"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={sectionForm.control}
                        name="subtitle"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Subtitle</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Section subtitle"
                                {...field}
                                value={field.value || ""}
                                data-testid="input-section-subtitle"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={sectionForm.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Detailed description or content"
                              {...field}
                              value={field.value || ""}
                              data-testid="textarea-section-description"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FormField
                        control={sectionForm.control}
                        name="backgroundColor"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Background Color</FormLabel>
                            <FormControl>
                              <Input 
                                type="color"
                                {...field}
                                value={field.value || "#ffffff"}
                                data-testid="input-background-color"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={sectionForm.control}
                        name="textColor"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Text Color</FormLabel>
                            <FormControl>
                              <Input 
                                type="color"
                                {...field}
                                value={field.value || "#000000"}
                                data-testid="input-text-color"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={sectionForm.control}
                        name="accentColor"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Accent Color</FormLabel>
                            <FormControl>
                              <Input 
                                type="color"
                                {...field}
                                value={field.value || "#3b82f6"}
                                data-testid="input-accent-color"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={sectionForm.control}
                        name="layout"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Layout</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value || "default"}>
                              <FormControl>
                                <SelectTrigger data-testid="select-layout">
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {LAYOUT_OPTIONS.map((layout) => (
                                  <SelectItem key={layout.value} value={layout.value}>
                                    {layout.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={sectionForm.control}
                        name="isActive"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                            <div className="space-y-0.5">
                              <FormLabel>Active</FormLabel>
                              <FormDescription>
                                Show this section on the landing page
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                data-testid="switch-section-active"
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>

                    <DialogFooter>
                      <Button 
                        type="submit" 
                        disabled={createSectionMutation.isPending || updateSectionMutation.isPending}
                        data-testid="button-save-section"
                      >
                        {createSectionMutation.isPending || updateSectionMutation.isPending 
                          ? "Saving..." 
                          : editingSectionId ? "Update Section" : "Create Section"
                        }
                      </Button>
                    </DialogFooter>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {sections
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((section) => (
              <Card key={section.id} className="relative">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        {SECTION_TYPES.find(t => t.value === section.sectionType)?.icon && (
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                            {React.createElement(
                              SECTION_TYPES.find(t => t.value === section.sectionType)!.icon,
                              { className: "w-4 h-4" }
                            )}
                          </div>
                        )}
                        <div>
                          <CardTitle className="text-lg">{section.sectionName}</CardTitle>
                          <Badge variant="outline" className="text-xs">
                            {SECTION_TYPES.find(t => t.value === section.sectionType)?.label}
                          </Badge>
                        </div>
                      </div>
                      {!section.isActive && (
                        <Badge variant="secondary">Disabled</Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveSection(section.id, 'up')}
                        disabled={section.sortOrder === 0}
                        data-testid={`button-move-up-${section.id}`}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveSection(section.id, 'down')}
                        data-testid={`button-move-down-${section.id}`}
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditSection(section)}
                        data-testid={`button-edit-section-${section.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteSectionMutation.mutate(section.id)}
                        data-testid={`button-delete-section-${section.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {section.title && (
                      <div>
                        <span className="text-sm font-medium">Title:</span>
                        <span className="text-sm text-gray-600 ml-2">{section.title}</span>
                      </div>
                    )}
                    {section.subtitle && (
                      <div>
                        <span className="text-sm font-medium">Subtitle:</span>
                        <span className="text-sm text-gray-600 ml-2">{section.subtitle}</span>
                      </div>
                    )}
                    {section.description && (
                      <div>
                        <span className="text-sm font-medium">Description:</span>
                        <span className="text-sm text-gray-600 ml-2">{section.description.substring(0, 100)}{section.description.length > 100 ? '...' : ''}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Feature Matrix Tab */}
        <TabsContent value="features" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Feature Comparison Matrix</h2>
            <Dialog>
              <DialogTrigger asChild>
                <Button data-testid="button-add-feature">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Feature
                </Button>
              </DialogTrigger>
              <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingFeatureId ? "Edit Feature" : "Add New Feature"}
                  </DialogTitle>
                  <DialogDescription>
                    Define how this feature appears across different pricing plans
                  </DialogDescription>
                </DialogHeader>
                
                <Form {...featureForm}>
                  <form onSubmit={featureForm.handleSubmit(onSubmitFeature)} className="space-y-4">
                    <FormField
                      control={featureForm.control}
                      name="featureCategory"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Category</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-feature-category">
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Core">Core</SelectItem>
                              <SelectItem value="AI">AI</SelectItem>
                              <SelectItem value="Collaboration">Collaboration</SelectItem>
                              <SelectItem value="Analytics">Analytics</SelectItem>
                              <SelectItem value="Integrations">Integrations</SelectItem>
                              <SelectItem value="Support">Support</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={featureForm.control}
                      name="featureName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Feature Name</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="e.g., Goal Tracking, Team Collaboration"
                              {...field}
                              data-testid="input-feature-name"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={featureForm.control}
                      name="featureDescription"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Brief description of this feature"
                              {...field}
                              value={field.value || ""}
                              data-testid="textarea-feature-description"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FormField
                        control={featureForm.control}
                        name="freeValue"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Free Plan</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="e.g., 5 goals, Basic"
                                {...field}
                                value={field.value || ""}
                                data-testid="input-free-value"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={featureForm.control}
                        name="proValue"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Pro Plan</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="e.g., Unlimited, Advanced"
                                {...field}
                                value={field.value || ""}
                                data-testid="input-pro-value"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={featureForm.control}
                        name="enterpriseValue"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Enterprise Plan</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="e.g., Custom, Enterprise"
                                {...field}
                                value={field.value || ""}
                                data-testid="input-enterprise-value"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={featureForm.control}
                      name="isHighlighted"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                          <div className="space-y-0.5">
                            <FormLabel>Highlighted Feature</FormLabel>
                            <FormDescription>
                              Show this feature prominently in comparisons
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              data-testid="switch-feature-highlighted"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <DialogFooter>
                      <Button 
                        type="submit" 
                        disabled={createFeatureMutation.isPending || updateFeatureMutation.isPending}
                        data-testid="button-save-feature"
                      >
                        {createFeatureMutation.isPending || updateFeatureMutation.isPending 
                          ? "Saving..." 
                          : editingFeatureId ? "Update Feature" : "Create Feature"
                        }
                      </Button>
                    </DialogFooter>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-4">
            {["Core", "AI", "Collaboration", "Analytics", "Integrations", "Support"].map((category) => {
              const categoryFeatures = features.filter(f => f.featureCategory === category);
              
              if (categoryFeatures.length === 0) return null;

              return (
                <Card key={category}>
                  <CardHeader>
                    <CardTitle className="text-lg">{category} Features</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {categoryFeatures
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map((feature) => (
                        <div 
                          key={feature.id} 
                          className={`border rounded-lg p-4 ${feature.isHighlighted ? 'border-primary bg-primary/5' : ''}`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-medium">{feature.featureName}</h4>
                                {feature.isHighlighted && (
                                  <Badge variant="secondary">Highlighted</Badge>
                                )}
                              </div>
                              {feature.featureDescription && (
                                <p className="text-sm text-gray-600 mb-3">{feature.featureDescription}</p>
                              )}
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                <div>
                                  <span className="font-medium">Free:</span>
                                  <span className="ml-2">{feature.freeValue || "—"}</span>
                                </div>
                                <div>
                                  <span className="font-medium">Pro:</span>
                                  <span className="ml-2">{feature.proValue || "—"}</span>
                                </div>
                                <div>
                                  <span className="font-medium">Enterprise:</span>
                                  <span className="ml-2">{feature.enterpriseValue || "—"}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditFeature(feature)}
                                data-testid={`button-edit-feature-${feature.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  // Delete feature mutation here
                                }}
                                data-testid={`button-delete-feature-${feature.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Pricing Templates & Industry Examples</h2>
            <Button data-testid="button-create-template">
              <Plus className="w-4 h-4 mr-2" />
              Save Current as Template
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <Card key={template.id} className="cursor-pointer hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{template.templateName}</CardTitle>
                      <CardDescription>
                        {template.industry} • {template.companySize}
                      </CardDescription>
                    </div>
                    <Badge variant="outline">
                      {template.usageCount} uses
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">{template.description}</p>
                  <Button 
                    className="w-full"
                    onClick={() => applyTemplateMutation.mutate(template.id)}
                    disabled={applyTemplateMutation.isPending}
                    data-testid={`button-apply-template-${template.id}`}
                  >
                    <File className="w-4 h-4 mr-2" />
                    {applyTemplateMutation.isPending ? "Applying..." : "Apply Template"}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo" className="space-y-6">
          <h2 className="text-xl font-semibold">SEO & Analytics Configuration</h2>
          
          <Card>
            <CardHeader>
              <CardTitle>Search Engine Optimization</CardTitle>
              <CardDescription>
                Configure meta tags, keywords, and analytics for your landing pages
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                SEO configuration tools will be available here. This includes meta descriptions, 
                Open Graph tags, structured data, and analytics integration.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}